
import { Post } from "@/lib/types";
import { Card } from "../ui/card";
import { Avatar } from "../ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect } from "react";
import { PostDialog } from "./PostDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CommentForm } from "../CommentForm";
import { PostActions } from "../PostActions";
import { PostContent } from "../PostContent";
import { useFriendEngagement } from "@/hooks/useFriendEngagement";
import { useEngagementIndicator } from "@/hooks/useEngagementIndicator";


interface PostCardProps {
  post: Post;
}

export const PostCard = ({ post }: PostCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [localPost, setLocalPost] = useState<Post>(post);
  const [hasLiked, setHasLiked] = useState(false);
  const [hasSupported, setHasSupported] = useState(false);
  const [supportCount, setSupportCount] = useState(0);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const { toast } = useToast();
  const friendEngagement = useFriendEngagement(localPost.id);
  

  useEffect(() => {
    const checkReactions = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Check if user has liked this post
        const { data: likes } = await supabase
          .from('post_reactions')
          .select('*')
          .eq('post_id', localPost.id)
          .eq('user_id', user.id)
          .eq('reaction_type', 'like');

        setHasLiked(likes && likes.length > 0);

        // Check if user has supported this post
        const { data: supports } = await supabase
          .from('post_reactions')
          .select('*')
          .eq('post_id', localPost.id)
          .eq('user_id', user.id)
          .eq('reaction_type', 'support');

        setHasSupported(supports && supports.length > 0);

        // Get total support count
        const { count } = await supabase
          .from('post_reactions')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', localPost.id)
          .eq('reaction_type', 'support');

        setSupportCount(count || 0);
      } catch (error) {
        console.error('Error checking reactions:', error);
      }
    };

    checkReactions();
  }, [localPost.id]);

  const handleOpenPost = () => {
    setIsDialogOpen(true);
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to like posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasLiked) {
        // Optimistic update
        setLocalPost(prev => ({
          ...prev,
          likes_count: (prev.likes_count || 0) + 1
        }));
        setHasLiked(true);

        // Add reaction to database
        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: localPost.id,
            user_id: user.id,
            reaction_type: 'like'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Post liked! 💖",
          description: "Thanks for spreading the love!",
        });
      }
    } catch (error) {
      console.error('Error liking post:', error);
      // Revert optimistic update
      setLocalPost(prev => ({
        ...prev,
        likes_count: (prev.likes_count || 0) - 1
      }));
      setHasLiked(false);
      toast({
        title: "Error",
        description: "Could not like the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSupport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to support posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasSupported) {
        // Optimistic update
        setSupportCount(prev => prev + 1);
        setHasSupported(true);

        // Add reaction to database
        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: localPost.id,
            user_id: user.id,
            reaction_type: 'support'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Support Given! 💜",
          description: "Your support means a lot to the community!",
        });
      }
    } catch (error) {
      console.error('Error supporting post:', error);
      // Revert optimistic update
      setSupportCount(prev => prev - 1);
      setHasSupported(false);
      toast({
        title: "Error",
        description: "Could not support the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCommentForm(!showCommentForm);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };

  const handleAddComment = async (content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to comment",
          variant: "destructive"
        });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const newComment = {
        post_id: localPost.id,
        user_id: user.id,
        content,
        created_at: new Date().toISOString()
      };

      const { data: commentData, error } = await supabase
        .from('comments')
        .insert([newComment])
        .select()
        .single();

      if (error) throw error;

      // Add the new comment to the local state
      setLocalPost(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          {
            id: commentData.id,
            content: content,
            user_id: user.id,
            created_at: commentData.created_at,
            profiles: {
              username: profile.username,
              avatar_url: ""
            }
          }
        ]
      }));

      toast({
        title: "Comment added! 💭",
        description: "Thanks for engaging in the conversation!",
      });

      setShowCommentForm(false);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Could not add comment. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  // Get background gradient based on category
  const getCategoryGradient = () => {
    switch (localPost.category) {
      case 'gratitude':
        return 'bg-gradient-to-br from-orange-100 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30';
      case 'support':
        return 'bg-gradient-to-br from-pink-100 to-red-50 dark:from-pink-900/30 dark:to-red-900/30';
      case 'encouragement':
        return 'bg-gradient-to-br from-green-100 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30';
      default:
        return 'bg-gradient-to-br from-blue-100 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30';
    }
  };

  return (
    <>
      <Card 
        className={`p-5 cursor-pointer hover:shadow-lg transition-all w-full rounded-3xl border-0 ${getCategoryGradient()}`}
        onClick={handleOpenPost}
      >
        <div className="flex flex-col w-full space-y-4">
          {/* Header with avatar, name, and category */}
          <div className="flex items-start justify-between w-full">
            <div className="flex items-start space-x-3">
              <Avatar className="w-12 h-12 ring-2 ring-pink-400 border-2 border-white shadow-md">
                <img 
                  src={localPost.profiles?.avatar_url || "https://images.unsplash.com/photo-1649972904349-6e44c42644a7"} 
                  alt={localPost.profiles?.username} 
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1649972904349-6e44c42644a7";
                  }}
                />
              </Avatar>
              <div className="flex flex-col">
                <p className="font-semibold text-lg">{localPost.profiles?.username}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(localPost.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
            {localPost.category && (
              <span className="text-sm px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm shadow-sm font-medium text-pink-600">{localPost.category}</span>
            )}
          </div>
          
          
          {/* Content */}
          <div className="w-full">
            <PostContent 
              content={localPost.content} 
              imageUrl={localPost.image_url}
              videoUrl={localPost.video_url}
              postId={localPost.id}
            />
          </div>
          
          {/* Actions */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3" onClick={(e) => e.stopPropagation()}>
            <PostActions
              onLike={handleLike}
              onComment={handleCommentClick}
              onSupport={handleSupport}
              onShare={handleShare}
              hasLiked={hasLiked}
              hasSupported={hasSupported}
              postId={localPost.id}
              content={localPost.content}
              friendsWhoLiked={friendEngagement.friendsWhoLiked}
              friendsWhoCommented={friendEngagement.friendsWhoCommented}
              friendsWhoSupported={friendEngagement.friendsWhoSupported}
            />
          </div>
          
          {/* Comment Form */}
          {showCommentForm && (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3" onClick={(e) => e.stopPropagation()}>
              <CommentForm
                onSubmit={handleAddComment}
                placeholder="Write a comment..."
                buttonText="Comment"
                postId={localPost.id || ''}
                postContent={localPost.content}
                postCategory={localPost.category}
              />
            </div>
          )}
        </div>
      </Card>

      <PostDialog
        post={localPost}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};
